import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from "recharts";
import { downloadFile } from "../../../redux/actions/stateFileAction";
import { openInNewTab } from "../../../utils/helpers";
import { useStyles } from "./styles";

const CodeReport = ({ state, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { primary, warning, yellow, success } = theme.palette;
  const { importStatus } = useSelector((state) => state?.stateFile);
  const [loading, setLoading] = useState(false);

  const graphData = [
    {
      value: importStatus?.terraformCountBeforeImport || 0,
      color: yellow[1],
      label: "Terraform Managed Resources Before import",
    },
    {
      value: importStatus?.terraformCount || 0,
      color: warning.main,
      label: "Terraform Managed Resources after import",
    },
    {
      value: importStatus?.newlyAdded || 0,
      color: success.main,
      label: `Newly added resources to Terraform state file for ${state.service.join(
        ","
      )} is having count`,
    },
    {
      value: importStatus?.allCount || 0,
      color: primary.main,
      label: "Total resources in selected region",
    },
  ];

  const onDownload = async () => {
    setLoading(true);
    if (importStatus?.data?.bucketName) {
      const payload = {
        bucketName: importStatus?.data?.bucketName,
        key: importStatus?.data?.key,
        region: importStatus?.data?.region,
        credentials: importStatus?.data?.credName,
      };
      await dispatch(downloadFile(payload, payload?.key));
    } else {
      openInNewTab(importStatus?.data);
    }
    setLoading(false);
  };

  const renderDetails = () => {
    const { credential, region, service = [] } = state;
    const contentData = [
      { label: "Account", value: credential?.accountNumber },
      { label: "Region", value: region?.region },
      { label: "Services", value: service.join(",") },
    ];
    return (
      <Box className={classes.dialogContent}>
        {contentData.map((obj) => {
          return (
            <Typography>
              {obj.label} : {obj.value}
            </Typography>
          );
        })}
      </Box>
    );
  };

  const renderFooter = () => {
    return (
      <Box className={classes.footerView}>
        <Typography>
          Click download for generated terraform code and statefile
        </Typography>
        <Button
          onClick={onDownload}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileDownloadOutlined />
            )
          }
          variant="contained"
          sx={{ minWidth: "125px" }}
        >
          Download
        </Button>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </Box>
    );
  };

  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    return (
      <Text
        x={x}
        y={y + 10}
        style={{ fontSize: "12px" }}
        fill={theme.palette.text.primary}
        textAnchor="middle"
        width="170"
        verticalAnchor="start"
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <Box>
      {renderDetails()}
      <Box className={classes.root}>
        <Box px={4} py={3}>
          <Box className={classes.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                barCategoryGap={14}
                data={graphData}
                margin={{ top: 20, right: 10, left: -10, bottom: 50 }}
              >
                <YAxis
                  axisLine={{ stroke: theme.palette.grey[300] }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  width={30}
                  interval={0}
                  tick={<CustomXAxisTick />}
                />
                <Bar
                  label={{ position: "top" }}
                  barSize={50}
                  dataKey="value"
                  radius={[2, 2, 0, 0]}
                >
                  {graphData.map((entry, index) => (
                    <Cell key={`code-report-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <>
            <Box my={1} display="flex" justifyContent="space-between">
              <Typography>
                Percentage of resources not managed by Terraform after import
              </Typography>
              <Typography>
                {importStatus?.percentageNotManaged || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={importStatus?.percentageNotManaged || 0}
              color="secondary"
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: (theme) => theme.palette.grey[200],
              }}
            />
          </>
        </Box>
        <Divider />
        {renderFooter()}
      </Box>
    </Box>
  );
};

export default CodeReport;
